import { useT } from '@powerportalspro/react';
import {
  GridLayout,
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  PagingMode,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// The sample contacts as cards beside a map of where they are, at address1_latitude and
// address1_longitude: the list scrolls, loading more contacts and their pins as its end comes into
// view. Clicking a card brings its contact into view on the map, and selecting several fits them
// all; a pin clicked selects its contact and opens its popup, linked to its page. The Open button is
// what makes the cards selectable.
//
// `showMapPinNumbers` numbers each card and its pin alike, so a contact on the map can be found in the
// list. `showMapPrintButton` puts a print button over the map: it prints the map with the cards listed
// under it, numbered as their pins, or saves the map as an image.
//
// The layout dropdown's Map only shows the map alone, with every contact the grid matches on it:
// loaded `mapPageSize` at a time (250 by default) up to `mapMaximumRows` (1,000 by default), the map
// saying so when there are more. A search narrows the pins as it narrows the cards.
//
// The map layouts need <MapsProvider> from @powerportalspro/react-maps around the app.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.GridMapLayoutMainDemo';

const CONTACT_URL = 'demos/contact';

const CONTACTS_VIEW_ID = '8d2c5f1a-4b7e-4c39-9a06-3e1f7b2d6c58';

// Where each contact is, on the card beside its pin.
const CONTACTS_FETCH_XML = `<fetch>
  <entity name='contact'>
    <attribute name='fullname' />
    <attribute name='parentcustomerid' />
    <attribute name='address1_city' />
    <attribute name='address1_stateorprovince' />
    <attribute name='emailaddress1' />
    <order attribute='fullname' />
    <filter>
      <condition attribute='statecode' operator='eq' value='0' />
    </filter>
  </entity>
</fetch>`;

export function GridMapLayoutMainSample() {
  const t = useT();

  const views = useMemo<ViewDefinition[]>(
    () => [
      {
        id: CONTACTS_VIEW_ID,
        tableName: 'contact',
        displayName: t(`${STRINGS_BASE}.view-contacts`),
        fetchXml: CONTACTS_FETCH_XML,
        columns: [
          { columnName: 'fullname', width: 200 },
          { columnName: 'parentcustomerid', width: 200 },
          { columnName: 'address1_city', width: 150 },
          { columnName: 'address1_stateorprovince', width: 120 },
          { columnName: 'emailaddress1', width: 225 },
        ],
      },
    ],
    [t],
  );

  return (
    <MainGrid
      tableName="contact"
      viewIds={[CONTACTS_VIEW_ID]}
      customViewDefinitions={views}
      defaultViewId={CONTACTS_VIEW_ID}
      defaultLayout={GridLayout.Map}
      allowChangingLayout
      cardImageColumn="entityimage"
      mapHeight="600px"
      showMapPinNumbers
      showMapPrintButton
      pagingMode={PagingMode.Virtualize}
      pageSize={20}
    >
      <GridToolbarItems>
        <NavigateOpenRecordGridButton urlFor={(record) => `${CONTACT_URL}?contactId=${record.id}`} />
      </GridToolbarItems>
    </MainGrid>
  );
}
